import { isEqual } from 'underscore';
import { logProgramChange } from '@shoflo/studio-redux/utils/sendToDatadogWithIds';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { isDifferentDirector } from 'actions/Broadcast/utils';
import setProgram from 'actions/Broadcast/program/setProgram';
import sceneLayouts, { SINGLE } from 'constants/SceneLayouts';
import setSceneLayout from 'actions/Broadcast/program/setSceneLayout';
import getUpdatedBannerVisibility from 'store/utils/getUpdatedBannerVisibility';
import updateBanners from '@shoflo/studio-redux/actions/banner/updateBanners';
import setCameraStatusFromProgramLayers from 'actions/Broadcast/attendees/setCameraStatusFromProgramLayers';

const logger = new StudioLogger('app.studio.director.syncProgram');

/**
 * Builds a new program replacing current program state.
 *
 * This is meant to be called after another logged in director changes the state, emitting
 * a BROADCAST_PROGRAM_CHANGED event, which necessitates that all other directors have their
 * programs updated.
 *
 * Care should be taken that the director who emits the change is excluded from the update, as
 * that would cause an infinite loop.
 */
export default ({ program: updatedProgram, notifyingDirector }) => {
  return (dispatch, getState) => {
    const {
      program,
      banners,
      meeting: { attendeeResponse }
    } = getState();
    const attendeeId = attendeeResponse?.Attendee?.AttendeeId;
    const isProgramEqual = isEqual(program, updatedProgram);
    const isDifferent = attendeeId
      ? isDifferentDirector(notifyingDirector, attendeeId)
      : null;
    if (!isProgramEqual) {
      logger.info('setProgram - received', {
        attendeeId,
        notifyingDirector,
        sceneId: updatedProgram?._id,
        sceneLayout: updatedProgram?.layout,
        isDifferentDirector: isDifferent
      });
      if (!attendeeId) return;
      if (isDifferent) {
        // Update camera statuses for sources in the incoming program
        // This ensures cameras are turned on/off to match the program state
        if (updatedProgram.layers) {
          dispatch(setCameraStatusFromProgramLayers(updatedProgram.layers));
          const updatedBanners = getUpdatedBannerVisibility(
            updatedProgram,
            banners
          );
          dispatch(updateBanners(updatedBanners));
        }

        // When program is updated by a scene the id is not a layout in that
        // scenario we want to get the layout from the program title.
        const layout = sceneLayouts.find(
          l => l.id === updatedProgram?._id || l.id === updatedProgram?.layout
        );
        dispatch(setSceneLayout(layout || SINGLE));
        /**
         * The syncProgram action is meant to synchronize local state w/
         * some other authority (e.g. the server, or another director)
         * so we don't want to emit the program change to the socket server
         * in this case. If we were to emit the program change,
         * we would run the risk of entering into an infinite loop
         * between other sources of authority. i.e Multiple directors
         */
        dispatch(setProgram(updatedProgram, true, { emitSocket: false }));
        logProgramChange(getState, 'syncProgram action');
      }
    }
  };
};
